"use client";
/**
 * The download + share card from the tool detail sidebar.
 *
 * PERF: this is the ONLY interactive part of a tool page, and it used to live inside a
 * "use client" component that wrapped the whole route -- so the article body, the related rail
 * and the CTA were all serialized into the RSC payload on top of the HTML they render, and then
 * hydrated. Everything else is now a server component (HRToolDetail.tsx); this card is the island.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
// sonner, not @/hooks/use-toast. The marketing layout mounts only the sonner Toaster
// (DeferredChrome.tsx:17,49); the Radix one lives solely in PlatformApp. A useToast()
// call on a public page dispatches into a reducer nothing renders, so the "Link copied"
// confirmation was invisible -- and with the download button gone, copying is this
// card's only action.
import { toast } from 'sonner';
import { copyToClipboard } from '@/lib/utils';
import { XTwitterIcon } from '@/components/ui/x-twitter-icon';
import { Share2, Facebook, Linkedin, Link2 } from 'lucide-react';
import { trackConversion } from '@/utils/conversionTracking';

interface Props {
  /** Already stripped of HTML by the server component. */
  title: string;
  slug: string;
}

/**
 * Sharing only -- there is deliberately no download here.
 *
 * This card used to lead with a "Download Free" button that opened `download_url`. That
 * column is NULL on every one of the 577 published rows in hr_tools_templates (as are
 * file_type and file_size, and download_count is 0 throughout), so the button could never
 * deliver a file for any resource on this route. It was removed rather than backed by a
 * generated document: the resource here IS the article on the page.
 *
 * /resources/[slug] is the other half of this pair and keeps its download, because it
 * generates a branded PDF from the article content instead of relying on a stored file.
 */
export function ToolActionsCard({ title, slug }: Props) {
  const [shareOpen, setShareOpen] = useState(false);

  const shareUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `https://hirebee.ai/hr-tool-and-template/${slug}`;

  // Secondary "Resource share" conversion, same event as /resources/[slug]. These
  // buttons recorded nothing at all before.
  const trackShare = (network: string) =>
    trackConversion('resource_share', 'Resource Share', { location: network });

  const shareTwitter = () => {
    trackShare('twitter');
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400',
    );
  };
  const shareFacebook = () => {
    trackShare('facebook');
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400',
    );
  };
  const shareLinkedin = () => {
    trackShare('linkedin');
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400',
    );
  };
  const copyLink = async () => {
    try {
      await copyToClipboard(shareUrl);
      toast.success('Link copied', { description: 'Resource link copied to clipboard.' });
      trackShare('copy');
    } catch {
      toast.error('Failed to copy the link');
    }
    setShareOpen(false);
  };

  return (
    /* relative z-20 so the share dropdown (below) paints above the Related Resources card: both
       Cards get their own stacking context from .macos-card's backdrop-blur, so without an
       explicit z-index the later sibling in DOM order would win regardless of the dropdown's
       own z-10. */
    <Card className="relative z-20">
      <CardContent className="p-6 space-y-4">
        {/* "Share", not "Get This Resource": nothing is handed over here any more, and a
            heading that promises a download above a card that only shares is how the old
            dead button read to a visitor. */}
        <div className="space-y-2">
          <h2 className="font-semibold text-lg">Share This Resource</h2>
          <p className="text-sm text-muted-foreground">
            100% free to read — no registration required.
          </p>
        </div>

        {/* Share toggle */}
        <div className="relative">
          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={() => setShareOpen((o) => !o)}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          {shareOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg p-3 z-10 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                Share this resource
              </p>
              <button
                onClick={shareTwitter}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted transition-colors"
              >
                <XTwitterIcon size={14} /> X (Twitter)
              </button>
              <button
                onClick={shareFacebook}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted transition-colors"
              >
                <Facebook className="h-4 w-4" /> Facebook
              </button>
              <button
                onClick={shareLinkedin}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted transition-colors"
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </button>
              <button
                onClick={copyLink}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded hover:bg-muted transition-colors"
              >
                <Link2 className="h-4 w-4" /> Copy Link
              </button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
